import { useState } from 'react';
import { LuArrowUpRight } from 'react-icons/lu';
import { Link } from 'react-router-dom';
import { Area, ComposedChart, Line, Tooltip, XAxis, YAxis } from 'recharts';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { formatCurrency, formatDate, formatPercent } from 'utils';
import { useAccountHistoricalPerformance, useAggregatedPerformanceReport } from 'views/portfolio/reporting-queries';

import { buildNetValueChart, getNetValueDateRange, NetValuePeriod } from './net-value';

interface NetValueCardProps {
    householdGUID: string;
    accountGUID: string;
    performancePath: string;
}

const VALUE_COLOR = '#3c8a95';
const INVESTED_COLOR = '#b59963';
const PERIODS = ['1Y', '3Y', 'All Time'] as const;

export const NetValueCard = ({ householdGUID, accountGUID, performancePath }: NetValueCardProps) => {
    const [period, setPeriod] = useState<NetValuePeriod>('All Time');
    const params = {
        householdGUID,
        groupByGuids: [accountGUID],
        ...getNetValueDateRange(period, new Date()),
    };
    const { data: history, isPending, isError } = useAccountHistoricalPerformance(params, { throwOnError: false });
    const {
        data: report,
        isPending: isReturnPending,
        isError: isReturnError,
    } = useAggregatedPerformanceReport(params, {
        throwOnError: false,
    });
    const chartData = buildNetValueChart(history ?? [], period);
    const latest = chartData.at(-1);
    const hasValues = chartData.some((point) => point.marketValue !== null);
    const unavailable = isPending || isError;
    const returnValue = report?.returnsPercentNet == null ? undefined : Number(report.returnsPercentNet);
    const showReturn = !isReturnPending && !isReturnError && returnValue !== undefined && Number.isFinite(returnValue);

    return (
        <Card className="border-border flex min-h-[32rem] min-w-0 flex-col rounded-2xl bg-white p-4 lg:p-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <h2 className="font-heading text-cocoa-1000 text-xl">Net Value</h2>
                <div className="flex items-center gap-3">
                    <ToggleGroup
                        type="single"
                        aria-label="Net value period"
                        value={period}
                        onValueChange={(value) => {
                            const selected = PERIODS.find((item) => item === value);
                            if (selected) setPeriod(selected);
                        }}
                        className="inline-flex rounded-xl bg-stone-200/70 p-1"
                    >
                        {PERIODS.map((value) => (
                            <ToggleGroupItem
                                key={value}
                                value={value}
                                className="text-cocoa-600 data-[state=on]:text-cocoa-1000 h-auto flex-none cursor-pointer rounded-lg px-4 py-2 font-medium first:rounded-lg last:rounded-lg hover:bg-white/40 data-[state=on]:bg-white data-[state=on]:shadow-sm"
                            >
                                {value}
                            </ToggleGroupItem>
                        ))}
                    </ToggleGroup>
                    <Button asChild variant="outline" size="icon" className="shrink-0 rounded-lg">
                        <Link to={performancePath} aria-label="View account performance">
                            <LuArrowUpRight className="h-5 w-5" />
                        </Link>
                    </Button>
                </div>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-4">
                {[
                    { label: 'Total value', amount: latest?.marketValue, color: VALUE_COLOR },
                    { label: 'Net invested', amount: latest?.netInvested, color: INVESTED_COLOR },
                ].map(({ label, amount, color }) => (
                    <div key={label}>
                        <div className="text-cocoa-500 flex items-center gap-2 text-[10px] font-medium tracking-wider uppercase">
                            <span className="h-0.5 w-4 shrink-0" style={{ backgroundColor: color }} />
                            {label}
                        </div>
                        <div className="text-cocoa-1000 mt-3 text-2xl font-medium tabular-nums">
                            {unavailable || amount == null ? '—' : formatCurrency(amount)}
                        </div>
                    </div>
                ))}
            </div>
            <div
                className={`mt-2 text-sm ${showReturn ? (returnValue < 0 ? 'text-red-600' : 'text-green-700') : 'text-cocoa-500'}`}
            >
                {showReturn
                    ? `${returnValue > 0 ? '+' : ''}${formatPercent(returnValue)} time-weighted return`
                    : '— time-weighted return'}
            </div>
            <div className="mt-6 min-h-60 flex-1">
                {unavailable || !hasValues ? (
                    <div
                        className="text-cocoa-400 flex h-full min-h-60 items-center justify-center text-sm"
                        role="status"
                    >
                        {isPending
                            ? 'Loading net value…'
                            : isError
                              ? 'Unable to load net value'
                              : 'No performance history available'}
                    </div>
                ) : (
                    <ComposedChart
                        responsive
                        style={{ width: '100%', height: '100%', minHeight: 240 }}
                        data={chartData}
                        margin={{ top: 8, right: 0, left: 0, bottom: 0 }}
                    >
                        <XAxis dataKey="date" hide />
                        <YAxis hide domain={['auto', 'auto']} />
                        <Tooltip
                            formatter={(value, name) => [formatCurrency(Array.isArray(value) ? value[0] : value), name]}
                            labelFormatter={(date) => formatDate(String(date), 'HUMAN')}
                            contentStyle={{ fontSize: 12, borderRadius: 8 }}
                        />
                        <Area
                            type="linear"
                            dataKey="marketValue"
                            name="Total value"
                            stroke={VALUE_COLOR}
                            strokeWidth={2}
                            fill="#e8f2f4"
                            fillOpacity={1}
                            dot={false}
                            activeDot={{ r: 4 }}
                            isAnimationActive={false}
                        />
                        <Line
                            type="linear"
                            dataKey="netInvested"
                            name="Net invested"
                            stroke={INVESTED_COLOR}
                            strokeWidth={2}
                            dot={false}
                            isAnimationActive={false}
                        />
                    </ComposedChart>
                )}
            </div>
            <p className="text-cocoa-400 mt-3 text-xs">
                {period === 'All Time'
                    ? 'Net invested is cumulative since inception.'
                    : 'Net invested is opening value plus net flows over the selected period.'}{' '}
                Month-end values; the current month uses the latest available date. Returns are net of fees.
            </p>
        </Card>
    );
};
